import { FormEvent, useEffect, useState } from 'react';
import {
  deactivateMember,
  fetchEmployeeCard,
  fetchEmployeeHistory,
  fetchEmployeePhotoUrl,
} from '../../api/org';
import {
  fetchUserLeaveBalance,
  upsertLeaveBalance,
  autoGrantLeaveBalance,
} from '../../api/leaveRequest';
import { companyRoleLabel, canConfirmInspection } from '../../types/companyRole';
import { useCompanyStore } from '../../store/companyStore';
import { EmployeeCard, EmployeeHistoryItem } from '../../types/org';
import {
  LeaveBalance,
  formatLeaveDays,
} from '../../types/leaveRequest';
import './OrgAdminPanel.css';

import { reportError } from '../../utils/notify';
interface EmployeeCardProps {
  userId: number;
  onBack: () => void;
  onEdit: () => void;
  onDeactivated: () => void;
}

type DetailTab = 'info' | 'history';
type HistoryFilter = 'ALL' | 'DEPARTMENT' | 'ROLE' | 'JOIN' | 'LEAVE';

const HISTORY_FILTERS: { key: HistoryFilter; label: string }[] = [
  { key: 'ALL', label: '전체' },
  { key: 'DEPARTMENT', label: '인사배치' },
  { key: 'ROLE', label: '역할' },
  { key: 'JOIN', label: '입사' },
  { key: 'LEAVE', label: '연차' },
];

function eventTypeLabel(type?: string | null) {
  switch (type) {
    case 'DEPARTMENT':
      return '인사배치';
    case 'ROLE':
      return '역할';
    case 'JOIN':
      return '입사';
    case 'DEACTIVATE':
      return '비활성화';
    case 'LEAVE':
      return '연차';
    default:
      return type || '이력';
  }
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function EmployeeCardView({ userId, onBack, onEdit, onDeactivated }: EmployeeCardProps) {
  const role = useCompanyStore((s) => s.role);
  const canManageLeave = canConfirmInspection(role);
  const currentYear = new Date().getFullYear();
  const [card, setCard] = useState<EmployeeCard | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deactivating, setDeactivating] = useState(false);
  const [tab, setTab] = useState<DetailTab>('info');
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>('ALL');
  const [histories, setHistories] = useState<EmployeeHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [leaveYear, setLeaveYear] = useState(currentYear);
  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [adjustGrantedDays, setAdjustGrantedDays] = useState('');
  const [leaveRemark, setLeaveRemark] = useState('');
  const [savingLeave, setSavingLeave] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchEmployeeCard(userId);
        setCard(data);
        if (data.hasPhoto) {
          objectUrl = await fetchEmployeePhotoUrl(userId);
          setPhotoUrl(objectUrl);
        } else {
          setPhotoUrl(data.profileImageUrl || null);
        }
        setError('');
      } catch {
        reportError(setError, '인사카드를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    void load();
    return () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [userId]);

  useEffect(() => {
    const loadBalance = async () => {
      try {
        const data = await fetchUserLeaveBalance(userId, leaveYear);
        setBalance(data);
        setAdjustGrantedDays('');
        setLeaveRemark('');
      } catch {
        setBalance(null);
      }
    };
    void loadBalance();
  }, [userId, leaveYear]);

  useEffect(() => {
    if (tab !== 'history') return;
    const loadHistory = async () => {
      try {
        setHistoryLoading(true);
        const list = await fetchEmployeeHistory(
          userId,
          historyFilter === 'ALL' ? undefined : historyFilter
        );
        setHistories(list);
      } catch {
        reportError(setError, '이력을 불러오지 못했습니다.');
      } finally {
        setHistoryLoading(false);
      }
    };
    void loadHistory();
  }, [tab, historyFilter, userId]);

  const handleDeactivate = async () => {
    if (!card) return;
    const name = card.displayName || card.nickname || `사용자 ${card.userId}`;
    const isGuest = card.role === 'GUEST';
    const ok = window.confirm(
      isGuest
        ? `'${name}' 게스트를 삭제할까요?\n회사에 더 이상 접근할 수 없으며, 관련 DB 데이터도 삭제됩니다.`
        : `'${name}' 님을 비활성화할까요?\n해당 회사에 더 이상 접근할 수 없습니다.`
    );
    if (!ok) return;
    try {
      setDeactivating(true);
      await deactivateMember(card.userId);
      onDeactivated();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '비활성화에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setDeactivating(false);
    }
  };

  const handleSaveLeave = async (event: FormEvent) => {
    event.preventDefault();
    const adjust = Number(adjustGrantedDays);
    const reason = leaveRemark.trim();
    if (!Number.isFinite(adjust) || adjust === 0) {
      reportError(setError, '부여 증감 일수를 확인해 주세요. (0 불가)');
      return;
    }
    if (!reason) {
      reportError(setError, '연차 부여 사유를 입력해 주세요.');
      return;
    }
    const currentGranted = Number(balance?.grantedDays ?? 0);
    const nextGranted = currentGranted + adjust;
    if (nextGranted < 0) {
      reportError(setError, '부여 일수는 0일 미만이 될 수 없습니다.');
      return;
    }
    try {
      setSavingLeave(true);
      const next = await upsertLeaveBalance({
        userId,
        leaveYear,
        adjustGrantedDays: adjust,
        grantedDays: nextGranted,
        remark: reason,
      });
      setBalance(next);
      setAdjustGrantedDays('');
      setLeaveRemark('');
      setError('');
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '연차 부여에 실패했습니다.');
    } finally {
      setSavingLeave(false);
    }
  };

  const handleAutoGrant = async () => {
    try {
      setSavingLeave(true);
      const next = await autoGrantLeaveBalance(userId, leaveYear);
      setBalance(next);
      setAdjustGrantedDays('');
      setLeaveRemark('');
      setError('');
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '자동 부여에 실패했습니다. 입사일을 먼저 등록해 주세요.');
    } finally {
      setSavingLeave(false);
    }
  };

  if (loading) {
    return <p className="contract-panel__hint">불러오는 중...</p>;
  }

  if (!card) {
    return (
      <div className="org-card">
        <p className="contract-panel__error">{error || '인사카드를 찾을 수 없습니다.'}</p>
        <button type="button" onClick={onBack}>
          목록으로
        </button>
      </div>
    );
  }

  const displayName = card.displayName || card.nickname || `사용자 ${card.userId}`;

  return (
    <div className="org-card">
      <header className="org-card__header">
        <div className="org-card__identity">
          <div className="org-card__photo">
            {photoUrl ? <img src={photoUrl} alt={displayName} /> : <span>{displayName.slice(0, 1)}</span>}
          </div>
          <div>
            <p className="org-admin__eyebrow">인사카드</p>
            <h2>{displayName}</h2>
            <p>
              {companyRoleLabel(card.role)}
              {card.jobTitle ? ` · ${card.jobTitle}` : ''}
              {card.departmentName ? ` · ${card.departmentName}` : ' · 미배치'}
            </p>
          </div>
        </div>
        <div className="org-card__actions">
          <button type="button" onClick={onEdit}>
            수정
          </button>
          <button
            type="button"
            className="is-danger"
            disabled={deactivating}
            onClick={() => void handleDeactivate()}
          >
            {deactivating ? '처리 중...' : card.role === 'GUEST' ? '삭제' : '비활성화'}
          </button>
          <button type="button" className="is-ghost" onClick={onBack}>
            목록으로
          </button>
        </div>
      </header>

      {error && <p className="contract-panel__error">{error}</p>}

      <div className="org-card__tabs">
        <button
          type="button"
          className={tab === 'info' ? 'is-active' : ''}
          onClick={() => setTab('info')}
        >
          기본정보
        </button>
        <button
          type="button"
          className={tab === 'history' ? 'is-active' : ''}
          onClick={() => setTab('history')}
        >
          이력
        </button>
      </div>

      {tab === 'info' ? (
        <div className="org-card__grid">
          <section>
            <h3>기본 정보</h3>
            <dl>
              <div>
                <dt>이름</dt>
                <dd>{card.displayName || card.nickname || '-'}</dd>
              </div>
              <div>
                <dt>사번</dt>
                <dd>{card.employeeNo || '-'}</dd>
              </div>
              <div>
                <dt>담당</dt>
                <dd>{card.jobTitle || '-'}</dd>
              </div>
              <div>
                <dt>역할</dt>
                <dd>{companyRoleLabel(card.role)}</dd>
              </div>
              <div>
                <dt>부서</dt>
                <dd>{card.departmentName || '미배치'}</dd>
              </div>
            </dl>
          </section>

          <section>
            <h3>연락 / 인사</h3>
            <dl>
              <div>
                <dt>이메일</dt>
                <dd>{card.email || '-'}</dd>
              </div>
              <div>
                <dt>연락처</dt>
                <dd>{card.phone || '-'}</dd>
              </div>
              <div>
                <dt>생년월일</dt>
                <dd>{card.birthDate || '-'}</dd>
              </div>
              <div>
                <dt>입사일</dt>
                <dd>{card.hireDate || '-'}</dd>
              </div>
              <div>
                <dt>주소</dt>
                <dd>{card.address || '-'}</dd>
              </div>
            </dl>
          </section>

          <section className="is-full">
            <div className="org-leave-head">
              <h3>연차 ({leaveYear})</h3>
              <select
                value={leaveYear}
                onChange={(e) => setLeaveYear(Number(e.target.value))}
              >
                {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                  <option key={y} value={y}>
                    {y}년
                  </option>
                ))}
              </select>
            </div>
            <div className="org-leave-summary">
              <div>
                <span>부여</span>
                <strong>{formatLeaveDays(balance?.grantedDays)}일</strong>
              </div>
              <div>
                <span>사용</span>
                <strong>{formatLeaveDays(balance?.usedDays)}일</strong>
              </div>
              <div className="is-remain">
                <span>잔여</span>
                <strong>{formatLeaveDays(balance?.remainingDays)}일</strong>
              </div>
            </div>
            {canManageLeave ? (
              <form className="org-leave-form" onSubmit={(e) => void handleSaveLeave(e)}>
                {balance?.suggestedGrantExplain ? (
                  <p className="org-leave-suggest is-span-2">
                    입사일 기준 제안: <strong>{formatLeaveDays(balance.suggestedGrantedDays)}일</strong>
                    {' · '}
                    {balance.suggestedGrantExplain}
                  </p>
                ) : null}
                <label>
                  증감 일수 *
                  <input
                    type="number"
                    step="0.5"
                    value={adjustGrantedDays}
                    onChange={(e) => setAdjustGrantedDays(e.target.value)}
                    placeholder="예: 3 또는 -1"
                    required
                  />
                </label>
                <p className="org-leave-suggest is-span-2">
                  입력한 일수만큼 기존 부여({formatLeaveDays(balance?.grantedDays)}일)에 더하거나 뺍니다.
                </p>
                <label className="is-span-2">
                  사유 *
                  <input
                    value={leaveRemark}
                    onChange={(e) => setLeaveRemark(e.target.value)}
                    placeholder="예: 입사 연차 추가 부여 / 오부여 정정"
                    required
                  />
                </label>
                <div className="org-leave-form__actions is-span-2">
                  <button
                    type="button"
                    className="is-ghost"
                    disabled={savingLeave}
                    onClick={() => void handleAutoGrant()}
                  >
                    입사일 기준 자동 부여
                  </button>
                  <button type="submit" disabled={savingLeave}>
                    {savingLeave ? '저장 중...' : '연차 부여 저장'}
                  </button>
                </div>
              </form>
            ) : (
              <p className="org-card__memo">연차 부여는 오너/팀장만 수정할 수 있습니다.</p>
            )}
          </section>

          <section className="is-full">
            <h3>메모</h3>
            <p className="org-card__memo">{card.memo || '등록된 메모가 없습니다.'}</p>
          </section>
        </div>
      ) : (
        <div className="org-history">
          <div className="org-history__filters">
            {HISTORY_FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={historyFilter === item.key ? 'is-active' : ''}
                onClick={() => setHistoryFilter(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {historyLoading ? (
            <p className="contract-panel__hint">이력을 불러오는 중...</p>
          ) : histories.length === 0 ? (
            <div className="org-history__empty">
              <strong>이력이 없습니다</strong>
              <p>인사배치·역할 변경·입사·연차 부여/사용 기록이 여기에 쌓입니다.</p>
            </div>
          ) : (
            <ul className="org-history__list">
              {histories.map((item) => (
                <li key={item.historyId}>
                  <div className="org-history__meta">
                    <span className="org-history__type">{eventTypeLabel(item.eventType)}</span>
                    <time>{formatDateTime(item.createdAt)}</time>
                  </div>
                  <strong>{item.title}</strong>
                  <p>{item.description || '-'}</p>
                  {(item.fromValue || item.toValue) && (
                    <em>
                      {item.fromValue || '-'} → {item.toValue || '-'}
                    </em>
                  )}
                  {item.createdByName && (
                    <span className="org-history__by">처리: {item.createdByName}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default EmployeeCardView;
